import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../Header';
import { appointmentMoveSteps } from '../bookingSteps';
import { SidebarIcon } from '../components/SidebarIcon';
import { API_BASE_URL } from '../constants';
import { t, type TranslationKey } from '../i18n/translations';
import './DateTimeEntryPage.css';

// TODO: use the one in backendInterface.ts:
interface AppointmentFull {
  when: string;
  duration?: number;
  practitionerIds: string[];
  procedureIds: (string | null)[];
  // existing but not used:
  // userIds: string[];
  // patientIds: string[];
  procedureName?: string; // NOT in backendInterface.AppointmentFull
  // practitionerName?: string;
  // id: string;
  // meetingId: string
  // type?: string;
  // [meetingInfo: string]: any;
  // [appointmentInfo: string]: any;
}

interface Procedure {
  id: string;
  name: string;
  duration: number;
  [ruleMap: string]: any;
}

interface FullPractitioner {
  //id: string; // DEPRECATED, use userId
  userId: string;
  firstName?: string;
  familyName?: string;
  title?: string;
}

const weekdayLabels = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const monthTranslationKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'] as const;
const procedureCacheKey = 'procedureCache';
const PractitionerCache = new Map<string, FullPractitioner>();

// Calendar dates and the API's timezone-less appointment strings use Swiss
// wall-clock values, independently of the patient's browser timezone.
const practiceClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Zurich', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
});
const getPracticeNow = (instant = new Date()) => {
  const parts = Object.fromEntries(practiceClock.formatToParts(instant).map(({ type, value }) => [type, value]));
  return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour), Number(parts.minute), Number(parts.second));
};
const readAppointmentDate = (when?: string) => {
  if (!when) return null;
  const date = new Date(when);
  if (Number.isNaN(date.getTime())) return null;
  return /(?:Z|[+-]\d{2}:?\d{2})$/i.test(when) ? getPracticeNow(date) : date;
};

const toDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatApiDate = (date: Date) => `${toDateKey(date)}T11:12:10`;
const formatLocalDateTime = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

const formatMinutes = (minutes: number) =>
  `${Math.floor(minutes / 60).toString().padStart(2, '0')}:${(minutes % 60).toString().padStart(2, '0')}`;

const excludePastSlotsForToday = (slots: number[], selectedDate: Date, currentTime: Date) => {
  const practiceTime = getPracticeNow(currentTime);
  if (toDateKey(selectedDate) < toDateKey(practiceTime)) return [];
  if (toDateKey(selectedDate) !== toDateKey(practiceTime)) {
    return slots;
  }

  return slots.filter((minutes) => {
    const slotTime = new Date(selectedDate);
    slotTime.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    return slotTime.getTime() >= practiceTime.getTime();
  });
};

const readPendingAppointmentFull = () => {
  if (localStorage.getItem('selectedPractitionerMode') === 'any') return null;
  try {
    const raw = localStorage.getItem('pendingAppointmentFull');
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as AppointmentFull | null;
    const procedureId = localStorage.getItem('selectedProcedureId')?.trim() || '';
    const practitionerId = localStorage.getItem('selectedPractitionerId')?.trim() || '';
    if (!parsed || !procedureId || !practitionerId ||
      parsed.procedureIds?.[0] !== procedureId || parsed.practitionerIds?.[0] !== practitionerId) {
      return null;
    }
    const duration = readProcedureCache()[procedureId]?.duration;
    return duration && parsed.duration && duration !== parsed.duration ? null : parsed;
  } catch {
    return null;
  }
};

const readProcedureCache = () => {
  try {
    const raw = localStorage.getItem(procedureCacheKey);
    if (!raw) {
      return {} as Record<string, Procedure>;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : ({} as Record<string, Procedure>);
  } catch {
    return {} as Record<string, Procedure>;
  }
};

const writeProcedureCache = (cache: Record<string, Procedure>) => {
  try {
    localStorage.setItem(procedureCacheKey, JSON.stringify(cache));
  } catch {
    // Cache writes are best-effort.
  }
};

const getNameInitials = (firstName?: string) =>
  String(firstName ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((namePart) => `${namePart.charAt(0)}.`)
    .join(' ');

const getPractitionerDisplayName = (practitioner?: FullPractitioner) => {
  if (!practitioner) return '';

  const title = String(practitioner.title ?? '').trim();
  const initials = getNameInitials(practitioner.firstName);
  const familyName = String(practitioner.familyName ?? '').trim();

  return [title, initials, familyName].filter(Boolean).join(' ').trim();
};

const formatDuration = (duration?: number) => {
  if (!duration || Number.isNaN(duration)) return '';
  return `${duration} Minuten`;
};

const translateProcedureName = (procedureName?: string) => {
  if (!procedureName) return 'Behandlung';
  const translatedName = t(procedureName as TranslationKey) || procedureName;
  return translatedName.charAt(0).toUpperCase() + translatedName.slice(1);
};

const readableHover = (serverHoverText: string) => {
  // the "from" and "to" hover texts:
  if (serverHoverText === 'N') {
    return t('no-appointments-available');
  }

  const [from, to] = serverHoverText.split('|').map((part) => part.trim());

  if (!from || !to) {
    return serverHoverText;
  }

  return `Der ausgewählte Termin ist Teil einer Terminserie und muss zwischen dem ${from} und ${to} stattfinden. Kein passender Termin dabei? Rufen Sie uns an: 052 625 46 25.`;
}

const readableHoverOLD = (serverHoverText: string) => {
  // the "before" and "after" hover texts:
  if (serverHoverText === 'N') {
    return '';
  }

  const head = serverHoverText.charAt(0);
  const tailStartIndex = serverHoverText.slice(1).search(/[+-]/);
  const tail = tailStartIndex === -1 ? '' : serverHoverText.slice(tailStartIndex + 1);
  const rest = serverHoverText.slice(1, tail ? serverHoverText.length - tail.length : undefined);
  //const tail0 = tail.charAt(0);
  const tailNr = tail.slice(1);

  var tage;
  if (tailNr === '1') {
    tage = '1 Tag';
  } else {
    tage = tailNr + ' Tage';
  }

  var intro = 'Dieser Termin ist Teil einer Terminserie und muss ';
  var main;
  if (head === '>') {
    main = 'min. ' + tage + ' nach dem ';
  } else if (head === '<') {
    main = 'max. ' + tage + ' nach dem ';
  } else if (head === ']') {
    main = 'max. ' + tage + ' vor dem ';
  } else if (head === '[') {
    main = 'min. ' + tage + ' vor dem ';
  } else {
    main = '?' + serverHoverText + '?';
  }

  // Test mode, return raw hover text:
  return serverHoverText;

  //return `${intro}${main}${rest} sein (${serverHoverText}). Kein passender Termin dabei? Rufen Sie uns an: 052 625 46 25.`
}

const readStoredPatientId = () => {
  const raw = localStorage.getItem('loginPatientId');
  try {
    return raw ? JSON.parse(raw) : '';
  } catch {
    return raw?.trim() || '';
  }
};

const startOfWeekMonday = (date: Date) => {
  const result = new Date(date);
  const day = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - day);
  result.setHours(0, 0, 0, 0);
  return result;
};

const addDays = (date: Date, days: number) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

const startOfDay = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

const datesInInterval = (startDate: Date, endDate: Date) => {
  const dates: Date[] = [];

  for (let date = new Date(startDate); date <= endDate; date = addDays(date, 1)) {
    dates.push(date);
  }

  return dates;
};

export function DateTimeEntryPage() {
  const context = ['selectedProcedureId', 'selectedPractitionerId', 'selectedPractitionerMode', 'loginPatientId', 'selectedAppointmentId']
    .map((key) => localStorage.getItem(key) || '');
  return <DateTimeEntryContent key={JSON.stringify(context)} />;
}

function DateTimeEntryContent() {
  const now = useMemo(() => getPracticeNow(), []);
  const [pendingAppointment, setPendingAppointment] = useState<AppointmentFull | null>(() => readPendingAppointmentFull());
  const [selectedDate, setSelectedDate] = useState<Date | null>(() => {
    const storedDate = readAppointmentDate(pendingAppointment?.when);
    return storedDate && storedDate >= now ? storedDate : null;
  });
  const [selectionRequest, setSelectionRequest] = useState(() => ({ date: selectedDate, manual: false }));
  const currentDateRef = useRef(selectedDate);
  currentDateRef.current = selectedDate;
  const [datesError, setDatesError] = useState('');
  const [slotsError, setSlotsError] = useState('');
  const [searchEmpty, setSearchEmpty] = useState(false);
  const [searchRange, setSearchRange] = useState('');
  const [procedureLoadError, setProcedureLoadError] = useState(false);
  const [monthOffset, setMonthOffset] = useState(() => selectedDate
    ? (selectedDate.getFullYear() - now.getFullYear()) * 12 + selectedDate.getMonth() - now.getMonth()
    : 0);
  const [selectedTime, setSelectedTime] = useState(() => {
    const storedDate = readAppointmentDate(pendingAppointment?.when);
    return storedDate && storedDate >= now
      ? `${storedDate.getHours().toString().padStart(2, '0')}:${storedDate.getMinutes().toString().padStart(2, '0')}`
      : '';
  });
  const [availableDateKeys, setAvailableDateKeys] = useState<string[]>([]);
  const [availableDatesLoaded, setAvailableDatesLoaded] = useState(false);
  const [dateHoverTexts, setDateHoverTexts] = useState<Record<string, string>>({});
  const [hoveredDateKey, setHoveredDateKey] = useState<string | null>(null);
  const [selectedDaySlots, setSelectedDaySlots] = useState<number[]>([]);
  const [selectedProcedure, setSelectedProcedure] = useState<Procedure | null>(() => {
    const selectedProcedureId = localStorage.getItem('selectedProcedureId')?.trim() || '';
    return selectedProcedureId ? readProcedureCache()[selectedProcedureId] ?? null : null;
  });
  const [practitionerName, setPractitionerName] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const anyPractitioner = localStorage.getItem('selectedPractitionerMode') === 'any';
  const selectedPractitionerId = anyPractitioner ? '' : localStorage.getItem('selectedPractitionerId')?.trim() || '';
  const selectedAppointmentId = localStorage.getItem('selectedAppointmentId')?.trim() || '';
  const navigate = useNavigate();

  // 260822:
  const selectedProcedureId = localStorage.getItem('selectedProcedureId')?.trim() || '';
  const appointmentName = translateProcedureName(selectedProcedure?.name);

  // before:
  //const selectedProcedureId =
  //  pendingAppointment?.procedureIds?.[0]?.trim() || localStorage.getItem('selectedProcedureId')?.trim() || '';
  //const appointmentName = translateProcedureName(pendingAppointment?.procedureName ?? selectedProcedure?.name);

  const appointmentDuration = selectedProcedure?.duration ?? pendingAppointment?.duration ?? 60;
  const procedureReady = selectedProcedure?.id === selectedProcedureId &&
    Number.isFinite(selectedProcedure.duration) && selectedProcedure.duration > 0;
  const appointmentDurationLabel = formatDuration(appointmentDuration);
  const displayedPractitionerName = anyPractitioner ? 'Beliebiger Behandler' : practitionerName || selectedPractitionerId || 'Behandler';
  const previousPage = localStorage.getItem('previousPage')?.trim() || '/appointments';
  const canChangePractitioner = previousPage === '/practitioners';

  useEffect(() => {
    try {
      const headerDefinition = JSON.parse(localStorage.getItem('headerDefinition') ?? 'null');
      const headerPhase = selectedAppointmentId ? '1' : Array.isArray(headerDefinition)
        ? headerDefinition.length === 3
          ? '1'
          : headerDefinition.length === 5
            ? '3'
            : null
        : null;

      if (headerPhase && localStorage.getItem('headerPhase') !== headerPhase) {
        localStorage.setItem('headerPhase', headerPhase);
        window.dispatchEvent(new Event('header-updated'));
      }
    } catch {
      // Keep the existing phase when the stored header definition is invalid.
    }
  }, [selectedAppointmentId]);

  const back = () => {
    navigate(previousPage);
  };

  const change = () => {
    localStorage.removeItem('selectedPractitionerId');
    localStorage.removeItem('pendingAppointmentFull');
    navigate('/procedures');
  };

  const next = async () => {

    console.log("next: ", selectedTime, submitting);

    if (!canContinue || !selectedDate || submitting) {
      return;
    }
    if (!excludePastSlotsForToday(selectedDaySlots, selectedDate, new Date())
      .some((minutes) => formatMinutes(minutes) === selectedTime)) {
      setSelectedTime('');
      return;
    }

    const [hours, minutes] = selectedTime.split(':').map(Number);
    const newDate = new Date(selectedDate);
    newDate.setHours(hours, minutes, 0, 0);

    console.log("DateTimeEntry:selectedProcedureId=" + JSON.stringify(localStorage.getItem('selectedProcedureId')));

    const pendingAppointmentFull = {
      when: formatLocalDateTime(newDate),
      duration: appointmentDuration,
      practitionerIds: [selectedPractitionerId],
      procedureIds: [selectedProcedureId],
      procedureName: selectedProcedure?.name ?? pendingAppointment?.procedureName,
    };

    localStorage.setItem('pendingAppointmentFull', JSON.stringify(pendingAppointmentFull));
    setPendingAppointment(pendingAppointmentFull);

    console.log("DateTimeEntry:pendingAppointmentFull=" + JSON.stringify(pendingAppointmentFull));

    const loginPatientId = readStoredPatientId();

    console.log("next: ", loginPatientId);

    if (!loginPatientId) {
      navigate('/new-patient-data');
      return;
    }

    navigate('/appointment-summary', { state: pendingAppointmentFull });
  };

  const dateClick = (_date: Date) => { };
  const timeClick = (_time: string) => { };

  const previousMonth = () => setMonthOffset((value) => value - 1);
  const nextMonth = () => setMonthOffset((value) => value + 1);

  const { displayWeeks, displayedMonth, monthLabel } = useMemo(() => {
    const month = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
    const firstVisibleWeekStart = startOfWeekMonday(month);
    const lastDayOfMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const lastVisibleWeekStart = startOfWeekMonday(lastDayOfMonth);
    const weekCount = Math.round(
      (lastVisibleWeekStart.getTime() - firstVisibleWeekStart.getTime()) / (7 * 24 * 60 * 60 * 1000)
    ) + 1;
    const visibleWeeks = Array.from({ length: weekCount }, (_, weekIndex) => {
      const weekStart = addDays(firstVisibleWeekStart, weekIndex * 7);
      return Array.from({ length: 7 }, (_, dayIndex) => addDays(weekStart, dayIndex));
    });
    const monthKey = monthTranslationKeys[month.getMonth()] as TranslationKey;

    return {
      displayWeeks: visibleWeeks,
      displayedMonth: month,
      monthLabel: `${t(monthKey)} ${month.getFullYear()}`,
    };
  }, [monthOffset, now]);

  const selectedDateKey = selectedDate ? toDateKey(selectedDate) : '';
  const previousSelectedDateKey = useRef(selectedDateKey);
  useEffect(() => {
    // Also reset time when availability invalidates a restored date and a
    // different bookable date is selected. A valid saved date preserves its time.
    if (previousSelectedDateKey.current !== selectedDateKey) {
      setSelectedTime('');
      previousSelectedDateKey.current = selectedDateKey;
    }
  }, [selectedDateKey]);
  const todayKey = toDateKey(now);
  const availableDateKeySet = useMemo(() => new Set(availableDateKeys), [availableDateKeys]);
  const morningTimes = selectedDaySlots.filter((minutes) => minutes < 12 * 60);
  const afternoonTimes = selectedDaySlots.filter((minutes) => minutes >= 12 * 60);
  const canContinue = Boolean(
    selectedDate && selectedDateKey >= todayKey && availableDatesLoaded &&
    availableDateKeySet.has(selectedDateKey) && !loadingSlots &&
    selectedDate && excludePastSlotsForToday(selectedDaySlots, selectedDate, new Date())
      .some((minutes) => formatMinutes(minutes) === selectedTime)
  );

  useEffect(() => {
    let ignore = false;

    const loadSelectedProcedure = async () => {
      console.log("selectedProcedureId=" + JSON.stringify(localStorage.getItem('selectedProcedureId')));

      setProcedureLoadError(false);
      if (!selectedProcedureId) {
        setProcedureLoadError(true);
        setSelectedProcedure(null);
        return;
      }

      const procedureCache = readProcedureCache();
      const cachedProcedure = procedureCache[selectedProcedureId];

      if (cachedProcedure?.id && cachedProcedure?.name) {
        setSelectedProcedure(cachedProcedure);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/procedures/${selectedProcedureId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          if (!ignore) setProcedureLoadError(true);
          return;
        }

        const procedure = (await response.json()) as Procedure;

        if (!ignore && procedure?.id && procedure?.name) {
          procedureCache[selectedProcedureId] = procedure;
          writeProcedureCache(procedureCache);
          setSelectedProcedure(procedure);
        } else if (!ignore) {
          setProcedureLoadError(true);
        }
      } catch {
        if (!ignore) {
          setSelectedProcedure(null);
          setProcedureLoadError(true);
        }
      }
    };

    loadSelectedProcedure();

    return () => {
      ignore = true;
    };
  }, [selectedProcedureId]);

  useEffect(() => {
    let ignore = false;

    const loadPractitioner = async () => {
      if (!selectedPractitionerId) {
        setPractitionerName('');
        return;
      }

      const cachedPractitioner = PractitionerCache.get(selectedPractitionerId);
      if (cachedPractitioner) {
        setPractitionerName(getPractitionerDisplayName(cachedPractitioner));
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/practitioners/full/${selectedPractitionerId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (!response.ok) {
          return;
        }

        const fullPractitioner = (await response.json()) as FullPractitioner;

        if (!ignore && fullPractitioner) {
          PractitionerCache.set(selectedPractitionerId, fullPractitioner);
          setPractitionerName(getPractitionerDisplayName(fullPractitioner));
        }
      } catch {
        if (!ignore) {
          setPractitionerName('');
        }
      }
    };

    loadPractitioner();

    return () => {
      ignore = true;
    };
  }, [selectedPractitionerId]);

  useEffect(() => {
    let ignore = false;
    const fetchAvailableDates = async () => {
      setAvailableDatesLoaded(false);
      setDatesError('');
      setAvailableDateKeys([]);
      setDateHoverTexts({});
      if (anyPractitioner) {
        setDatesError('Die automatische Behandlerauswahl ist derzeit nicht verfügbar. Bitte wählen Sie eine konkrete Person aus.');
        return;
      }
      if (!selectedPractitionerId || !selectedProcedureId) {
        setDatesError('Bitte wählen Sie zuerst Behandlung und Behandler aus.');
        return;
      }
      if (!procedureReady) {
        if (procedureLoadError) setDatesError('Die Behandlungsdauer konnte nicht geladen werden.');
        return;
      }
      const startDate = startOfDay(now);
      const currentAppointmentDate = selectedAppointmentId && pendingAppointment?.when
        ? readAppointmentDate(pendingAppointment.when)
        : null;
      const intervalAnchor = currentAppointmentDate && !Number.isNaN(currentAppointmentDate.getTime())
        && currentAppointmentDate > startDate
        ? currentAppointmentDate
        : startDate;
      const minimumEndDate = addDays(startOfDay(intervalAnchor), 28);
      const displayedEndDate = displayWeeks.flat().at(-1);
      const endDate = displayedEndDate && displayedEndDate > minimumEndDate
        ? displayedEndDate
        : minimumEndDate;
      const selectableDates = datesInInterval(startDate, endDate);
      const patientId = readStoredPatientId();

      console.log("fetchAvailableDates: startDate=" + startDate + " endDate=" + endDate);
      //console.log("DateTimeEntry.selectableDates=" + selectableDates);

      setSearchRange(`${toDateKey(startDate).split('-').reverse().join('.')} bis ${toDateKey(endDate).split('-').reverse().join('.')}`);
      const token = localStorage.getItem('token');

      try {
        let response;
        if (patientId) {

          response = await fetch('/api/appointments/possible-date-infos', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              patientId,
              practitionerId: selectedPractitionerId,
              duration: String(appointmentDuration),
              startDate: formatApiDate(startDate),
              endDate: formatApiDate(endDate),
              appointmentId: selectedAppointmentId
            }),
          });

        } else {
          console.log("fetchAvailableDates: new Patient");

          response = await fetch('/api/appointments/new-patient-date-infos', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              practitionerId: selectedPractitionerId,
              duration: String(appointmentDuration),
              startDate: formatApiDate(startDate),
              endDate: formatApiDate(endDate),
            }),
          });

        }

        if (!response.ok) throw new Error('Verfügbarkeitsabfrage fehlgeschlagen.');
        const infos: unknown = await response.json();
        if (!Array.isArray(infos) || infos.some((info) => typeof info !== 'string' || !info.trim()) ||
          (infos.length > 0 && infos.length < selectableDates.length)) {
          throw new Error('Unvollständige oder ungültige Tagesverfügbarkeiten.');
        }
        if (ignore) return;
        setAvailableDateKeys(selectableDates.filter((_, index) => infos[index] === 'Y').map(toDateKey));
        setDateHoverTexts(Object.fromEntries(selectableDates
          .map((date, index) => [toDateKey(date), infos[index]])
          .filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1] !== 'Y')
          .map(([key, text]) => [key, readableHover(text)])));
        setAvailableDatesLoaded(true);
      } catch {
        if (!ignore) {
          setDatesError('Die Verfügbarkeiten konnten nicht zuverlässig geladen werden. Bitte versuchen Sie es erneut.');
        }
      }
    };
    fetchAvailableDates();
    return () => { ignore = true; };
  }, [appointmentDuration, procedureReady, procedureLoadError, displayWeeks, now,
    pendingAppointment?.when, selectedAppointmentId, selectedPractitionerId, selectedProcedureId, anyPractitioner]);

  useEffect(() => {
    let ignore = false;
    const findBookableDate = async () => {
      setLoadingSlots(true);
      setSelectedDaySlots([]);
      setSlotsError('');
      setSearchEmpty(false);
      if (datesError) {
        setLoadingSlots(false);
        setSelectedDate(null);
        setSelectedTime('');
        return;
      }
      if (!availableDatesLoaded) return;

      const manualDate = selectionRequest.manual ? selectionRequest.date : null;
      const preferredDate = manualDate || currentDateRef.current || selectionRequest.date;
      const candidates = availableDateKeys.filter((key) => key >= toDateKey(getPracticeNow()));
      const preferredKey = preferredDate ? toDateKey(preferredDate) : '';
      const keys = manualDate ? [preferredKey] : [
        ...(candidates.includes(preferredKey) ? [preferredKey] : []),
        ...candidates.filter((key) => key !== preferredKey),
      ];
      try {
        for (const key of keys) {
          if (!availableDateKeySet.has(key)) continue;
          const [year, month, day] = key.split('-').map(Number);
          const date = new Date(year, month - 1, day);
          const token = localStorage.getItem('token');
          const response = await fetch('/api/appointments/possible', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ patientId: readStoredPatientId(), practitionerId: selectedPractitionerId,
              duration: String(appointmentDuration), date: formatApiDate(date) }),
          });
          if (!response.ok) throw new Error('Uhrzeitenabfrage fehlgeschlagen.');
          const result: unknown = await response.json();
          if (!Array.isArray(result) || result.some((slot) => typeof slot !== 'number' ||
            !Number.isInteger(slot) || slot < 0 || slot >= 24 * 60)) {
            throw new Error('Ungültige Uhrzeiten.');
          }
          if (ignore) return;
          const validSlots = excludePastSlotsForToday(result, date, new Date());
          if (validSlots.length || manualDate) {
            const sameDate = key === (currentDateRef.current ? toDateKey(currentDateRef.current) : '');
            setSelectedDate(date);
            setSelectedDaySlots(validSlots);
            setSelectedTime((current) => sameDate && validSlots.some((minutes) => formatMinutes(minutes) === current) ? current : '');
            if (!manualDate && !sameDate) {
              setMonthOffset((date.getFullYear() - now.getFullYear()) * 12 + date.getMonth() - now.getMonth());
            }
            return;
          }
        }
        if (!ignore) {
          setSelectedDate(manualDate);
          setSelectedTime('');
          setSearchEmpty(!manualDate);
        }
      } catch {
        if (!ignore) {
          setSelectedDate(manualDate);
          setSelectedTime('');
          setSlotsError('Die freien Uhrzeiten konnten nicht zuverlässig ermittelt werden. Bitte versuchen Sie es erneut.');
        }
      } finally {
        if (!ignore) setLoadingSlots(false);
      }
    };
    findBookableDate();
    return () => { ignore = true; };
  }, [availableDatesLoaded, availableDateKeys, availableDateKeySet, datesError,
    selectedPractitionerId, selectedProcedureId, appointmentDuration, selectionRequest, now]);

  return (
    <div className="booking-date-time min-h-screen bg-[#fbf9f5] text-slate-900">
      <Header compact steps={selectedAppointmentId ? appointmentMoveSteps : undefined} phase={selectedAppointmentId ? 1 : undefined} />
      <div className="booking-date-time__content mx-auto max-w-[1360px] px-6 py-12">
        <div className="booking-date-time__layout grid gap-8 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="booking-date-time__selection self-start rounded-3xl border border-[#efe7da] bg-white px-6 py-6 shadow-[0_14px_30px_rgba(19,19,19,0.04)] lg:col-start-1">
            <p className="text-sm font-semibold text-[#d0bb91]">Ihre Auswahl</p>

            <div className="mt-8 space-y-6">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fbf8f1] text-[#c5b18d]">
                  <SidebarIcon name="document" className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <p className="booking-selection__value whitespace-normal [overflow-wrap:anywhere] text-[18px] font-semibold leading-6 text-slate-800">{appointmentName}</p>
                  {appointmentDurationLabel ? (
                    <p className="mt-0.5 text-[15px] font-medium leading-5 text-slate-400">{appointmentDurationLabel}</p>
                  ) : null}
                </div>
              </div>

              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fcfbf8] text-[#c5b18d]">
                  <SidebarIcon name="profile" className="h-5 w-5" />
                </span>
                <p className="booking-selection__value min-w-0 flex-1 whitespace-normal [overflow-wrap:anywhere] pt-1.5 text-[18px] font-semibold leading-6 text-slate-800">
                  {displayedPractitionerName}
                </p>
              </div>
            </div>

            {canChangePractitioner ? (
              <button
                type="button"
                onClick={change}
                className="mt-8 inline-flex h-9 w-full cursor-pointer items-center justify-center rounded-full border border-[#ebe7df] bg-white px-4 text-sm font-semibold text-slate-500 transition hover:border-[#d9c39b] hover:bg-[#fbf8f1] hover:text-slate-700"
              >
                Ändern
              </button>
            ) : null}
          </aside>

          <div className="overflow-hidden rounded-3xl border border-[#efe7da] bg-white shadow-[0_14px_30px_rgba(19,19,19,0.05)] lg:col-start-2">
            <div className="booking-date-time__panels grid md:grid-cols-2">
              <section className="booking-date-time__calendar p-8 md:p-10">
                <div className="booking-date-time__calendar-heading flex items-center justify-between">
                  <h1 className="text-3xl font-semibold text-slate-900">Datum wählen</h1>
                  <div className="flex items-center gap-4 text-3xl text-slate-400">
                    <button type="button" onClick={previousMonth} aria-label="Vorheriger Monat" className="cursor-pointer leading-none transition hover:text-[#bca477]">
                      {'<'}
                    </button>
                    <button type="button" onClick={nextMonth} aria-label="Nächster Monat" className="cursor-pointer leading-none transition hover:text-[#bca477]">
                      {'>'}
                    </button>
                  </div>
                </div>

                <p className="mt-10 text-center text-3xl font-semibold leading-none text-slate-800">{monthLabel}</p>

                <div className="booking-date-time__days mt-8 grid grid-cols-7 gap-y-4 text-center">
                  {weekdayLabels.map((label) => (
                    <span key={label} className="text-sm font-semibold text-slate-400">
                      {label}
                    </span>
                  ))}

                  {displayWeeks.flat().map((date) => {
                    const dateKey = toDateKey(date);
                    const isOutsideDisplayedMonth =
                      date.getMonth() !== displayedMonth.getMonth() ||
                      date.getFullYear() !== displayedMonth.getFullYear();
                    const isSelected = dateKey === selectedDateKey;
                    const isPast = dateKey < todayKey;
                    const hasAvailableSlots = availableDateKeySet.has(dateKey);
                    const hoverText = dateHoverTexts[dateKey];
                    //const hoverText = t('no-appointments-available');
                    //const hoverText = dateHoverTexts[dateKey] ?? (!isPast ? t('no-appointments-available') : '');
                    //const hoverText = dateHoverTexts[dateKey] ?? (!isPast && !hasAvailableSlots ? t('no-appointments-available') : '');
                    const isDisabled = isPast;

                    // console.log(date + " dateHoverTexts=" + JSON.stringify(dateHoverTexts));

                    let className =
                      'mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl text-xl font-medium transition disabled:cursor-not-allowed';
                    if (!isDisabled && hasAvailableSlots) {
                      className += ' cursor-pointer';
                      if (!isSelected) {
                        className += ' hover:bg-[#fbf8f1] hover:shadow-[inset_0_0_0_1px_#d9c39b]';
                      }
                    } else if (!isDisabled) {
                      className += ' cursor-default';
                    }
                    if (isSelected) {
                      className += ' bg-[#cab489] text-white';
                    } else if (isDisabled) {
                      className += ' text-slate-200';
                    } else if (hasAvailableSlots) {
                      className += isOutsideDisplayedMonth ? ' text-slate-400' : ' text-slate-800';
                      //className += ' border border-[#eadfce] text-slate-800';
                      //} else if (inCurrentMonth) {
                      //  className += ' text-slate-500';
                    } else {
                      className += ' text-slate-300';
                    }

                    return (
                      <div key={dateKey} className="relative mx-auto">
                        <button
                          type="button"
                          className={className}
                          disabled={isDisabled}
                          aria-pressed={isSelected}
                          data-available={!isDisabled && hasAvailableSlots}
                          onMouseEnter={() => {
                            if (!isPast && !hasAvailableSlots && hoverText) {
                              setHoveredDateKey(dateKey);
                            }
                          }}
                          onMouseLeave={() => setHoveredDateKey((current) => (current === dateKey ? null : current))}
                          onClick={() => {
                            if (isPast || !hasAvailableSlots) return;
                            setSelectedTime('');
                            setSelectedDaySlots([]);
                            setSelectedDate(new Date(date));
                            setSelectionRequest({ date: new Date(date), manual: true });
                            dateClick(date);
                          }}
                        >
                          {date.getDate()}
                        </button>
                        {hoveredDateKey === dateKey && hoverText && (
                          <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-max max-w-48 -translate-x-1/2 rounded-lg border border-[#eadfce] bg-white px-3 py-2 text-left text-sm font-medium text-slate-700 shadow-lg">
                            {hoverText}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

              </section>

              <section className="border-t border-[#efefef] p-8 md:border-l md:border-t-0 md:p-10">
                <h2 className="text-3xl font-semibold text-slate-900">Uhrzeit wählen</h2>
                {datesError || slotsError ? (
                  <p className="mt-4 text-sm text-slate-500" role="alert">{datesError || slotsError}</p>
                ) : loadingSlots ? (
                  <p className="mt-4 text-sm text-slate-500" role="status">Ermittle verfügbare Termine...</p>
                ) : searchEmpty ? (
                  <p className="mt-4 text-sm text-slate-500" role="status">Im abgefragten Zeitraum {searchRange} wurden keine freien Termine gefunden.</p>
                ) : null}

                <p className="mt-8 text-base font-semibold text-slate-400">Vormittag</p>
                <div className="booking-date-time__times mt-5 grid grid-cols-2 gap-4">
                  {morningTimes.map((minutes) => {
                    const time = formatMinutes(minutes);
                    const isSelected = selectedTime === time;
                    return (
                      <button
                        key={minutes}
                        type="button"
                        aria-pressed={isSelected}
                        className={`h-14 cursor-pointer rounded-2xl border text-xl font-medium transition disabled:cursor-not-allowed ${isSelected
                          ? 'border-[#cab489] bg-[#cab489] text-white'
                          : 'border-[#e9e9e9] bg-white text-slate-700 enabled:hover:border-[#d9c39b] enabled:hover:bg-[#fbf8f1]'
                          }`}
                        onClick={() => {
                          setSelectedTime(time);
                          timeClick(time);
                        }}
                      >
                        {time}
                      </button>
                    );
                  })}
                  {morningTimes.length === 0 && (
                    <p className="col-span-2 text-lg text-slate-400">
                      {loadingSlots ? 'Lade freie Zeiten...' : 'Keine freien Zeiten am Vormittag.'}
                    </p>
                  )}
                </div>

                <p className="mt-8 text-base font-semibold text-slate-400">Nachmittag</p>
                <div className="booking-date-time__times mt-5 grid grid-cols-2 gap-4">
                  {afternoonTimes.map((minutes) => {
                    const time = formatMinutes(minutes);
                    const isSelected = selectedTime === time;
                    return (
                      <button
                        key={minutes}
                        type="button"
                        aria-pressed={isSelected}
                        className={`h-14 cursor-pointer rounded-2xl border text-xl font-medium transition disabled:cursor-not-allowed ${isSelected
                          ? 'border-[#cab489] bg-[#cab489] text-white'
                          : 'border-[#e9e9e9] bg-white text-slate-700 enabled:hover:border-[#d9c39b] enabled:hover:bg-[#fbf8f1]'
                          }`}
                        onClick={() => {
                          setSelectedTime(time);
                          timeClick(time);
                        }}
                      >
                        {time}
                      </button>
                    );
                  })}
                  {afternoonTimes.length === 0 && (
                    <p className="col-span-2 text-lg text-slate-400">
                      {loadingSlots ? 'Lade freie Zeiten...' : 'Keine freien Zeiten am Nachmittag.'}
                    </p>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>

        <div className="booking-date-time__actions mt-10 flex flex-col items-stretch justify-end gap-4 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={back}
            className="inline-flex h-14 min-w-[180px] cursor-pointer items-center justify-center gap-3 rounded-xl border border-[#d8d8d8] bg-white px-6 text-lg font-semibold text-slate-700 transition hover:border-[#d9c39b] hover:bg-[#fbf8f1]"
          >
            <span>←</span>
            <span>Zurück</span>
          </button>
          <button
            type="button"
            onClick={next}
            disabled={!canContinue || submitting}
            className="inline-flex h-14 min-w-[180px] items-center justify-center gap-3 rounded-xl border border-[#cab489] bg-[#cab489] px-6 text-lg font-semibold text-white transition enabled:cursor-pointer enabled:hover:border-[#bca477] enabled:hover:bg-[#bca477] disabled:cursor-not-allowed"
          >
            <span>Weiter</span>
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  );
}
